/**
 * SetPasswordView (TEAM-PROVISION) — the public page an invite or password-reset
 * email link lands on (app_url/set-password). Supabase auto-detects the invite/
 * recovery token in the URL and establishes a short-lived session; the new staff
 * member (or anyone resetting) sets their password here, then we drop them into
 * Pegasus. No login required to reach it (the token IS the auth).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Check, ShieldCheck, Eye, EyeOff } from "lucide-react";
import { supabase } from "@/lib/supabase";

export function SetPasswordView() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [hasSession, setHasSession] = useState(false);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    // The supabase client parses the invite/recovery token from the URL on load
    // and fires an auth event with the session. Listen + also check immediately.
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setHasSession(!!session);
      setReady(true);
    });
    supabase.auth.getSession().then(({ data }) => {
      setHasSession(!!data.session);
      setReady(true);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const submit = async () => {
    setErr(null);
    if (pw.length < 8) { setErr("Use at least 8 characters."); return; }
    if (pw !== pw2) { setErr("The two passwords don't match."); return; }
    setBusy(true);
    const { error } = await supabase.auth.updateUser({ password: pw });
    setBusy(false);
    if (error) { setErr(error.message); return; }
    setDone(true);
    setTimeout(() => navigate("/"), 1300);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-sm rounded-2xl border border-hairline bg-white p-7 shadow-e2">
        <div className="mb-5 flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-600 text-white"><ShieldCheck className="h-5 w-5" /></span>
          <div>
            <div className="text-sm font-semibold text-slate-900">Sedgwick Heating · Pegasus</div>
            <div className="text-xs text-slate-500">Set your password</div>
          </div>
        </div>

        {!ready ? (
          <div className="flex items-center gap-2 py-6 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Checking your link…</div>
        ) : done ? (
          <div className="flex items-center gap-2 py-6 text-sm font-medium text-emerald-600"><Check className="h-4 w-4" /> Password set — taking you in…</div>
        ) : !hasSession ? (
          <p className="py-4 text-sm text-slate-600">
            This link is invalid or has expired. Ask your manager to resend your invite or password reset from the Team screen.
          </p>
        ) : (
          <div className="space-y-3">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-slate-500">New password</span>
              <div className="relative">
                <input
                  type={show ? "text" : "password"}
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  placeholder="At least 8 characters"
                  className="h-10 w-full rounded-lg border border-hairline bg-white px-3 pr-9 text-sm text-slate-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
                />
                <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" tabIndex={-1}>
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-slate-500">Confirm password</span>
              <input
                type={show ? "text" : "password"}
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
                placeholder="Re-enter it"
                className="h-10 w-full rounded-lg border border-hairline bg-white px-3 text-sm text-slate-700 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
              />
            </label>
            {err && <p className="text-xs text-red-600">{err}</p>}
            <button
              type="button"
              onClick={submit}
              disabled={busy || !pw || !pw2}
              className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-700 disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              {busy ? "Saving…" : "Set password & sign in"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
